import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import style from './side-panel-resize-handle.module.scss';

interface Props {
  open: boolean;
  // Selector for the SidePanel root (e.g. ".change-match-sidepanel"). The
  // class must be set via the SidePanel's `className` prop.
  panelSelector: string;
  // Selector matching the SidePanel's `selectorPageContent` so the page
  // content margin tracks the new width when slideIn is enabled.
  pageContentSelector?: string;
  defaultWidthPx: number;
  minWidthPx?: number;
  maxWidthPx?: number;
  // Show a persistent, always-visible grip instead of the hover-only strip.
  showGrip?: boolean;
}

export const SidePanelResizeHandle = ({
  open,
  panelSelector,
  pageContentSelector,
  defaultWidthPx,
  minWidthPx = 320,
  maxWidthPx = 1200,
  showGrip = false,
}: Props) => {
  const [width, setWidth] = useState<number>(defaultWidthPx);
  const [dragging, setDragging] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;

  useEffect(() => {
    if (open) setWidth(defaultWidthPx);
  }, [open, defaultWidthPx]);

  useEffect(() => {
    if (!open) return;
    const panel = document.querySelector(panelSelector) as HTMLElement | null;
    const pageContent = pageContentSelector
      ? (document.querySelector(pageContentSelector) as HTMLElement | null)
      : null;
    if (panel) {
      panel.style.setProperty('--c4p-side-panel-modified-size', `${width}px`);
    }
    if (pageContent) {
      pageContent.style.marginInlineEnd = `${width}px`;
    }
  }, [open, width, panelSelector, pageContentSelector]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setDragging(true);
      const onMove = (ev: MouseEvent) => {
        const next = Math.max(
          minWidthPx,
          Math.min(maxWidthPx, window.innerWidth - ev.clientX)
        );
        setWidth(next);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        setDragging(false);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [minWidthPx, maxWidthPx]
  );

  if (!open) return null;

  return createPortal(
    <>
      <div
        className={`${style.handle} ${showGrip ? style.visible : ''} ${
          dragging ? style.dragging : ''
        }`}
        style={{ right: `${width - 3}px` }}
        onMouseDown={onMouseDown}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize side panel"
      >
        {showGrip && <span className={style.grip} aria-hidden="true" />}
      </div>
      {dragging && <div className={style.dragOverlay} />}
    </>,
    document.body
  );
};
